'use client';

import Image from 'next/image';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import React from 'react';

import Link from 'next/link';

import {
  Box,
  Button,
  chakra,
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  HStack,
  IconButton,
  Link as LinkExternal,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';

import logout from '@/actions/auth/logout';
import { useT } from '@/app/i18n/client';
import { ids } from '@/data/e2eTestTokens';
import { MdClose, MdMenu } from 'react-icons/md';

interface HeaderProps {
  isLogin: boolean;
  name?: string | null;
  phone?: string | null;
}

interface MenuProps {
  isLogin: boolean;
  name?: string | null;
  phone?: string | null;
}

interface LanguageMenuProps {}

export default function Header({ isLogin, name, phone }: HeaderProps): React.ReactElement {
  const isNewMember = !name || !phone;
  const { t } = useT();

  return (
    <CustomHeader>
      <HStack
        w="100%"
        h="50px"
        minH="50px"
        px="16px"
        align="center"
        justify="space-between"
        gap="16px"
      >
        <Link href={isLogin && isNewMember ? '#' : '/home'}>
          <Image
            src="/assets/ugo_logo.svg"
            alt="UGO"
            priority
            width={80}
            height={50}
          />
        </Link>
        <div style={{ flex: 1 }} />
        <Link href="/orders">
          <Button
            size="small"
            px="4"
            py="2"
            borderRadius="36px"
            mx="6px"
          >
            <Text
              textStyle="p4"
              fontWeight="500"
            >
              {t('header.myOrder')}
            </Text>
          </Button>
        </Link>
        <LanguageMenu />
        {isLogin && isNewMember ? null : (
          <DrawerMenu
            isLogin={isLogin}
            name={name}
            phone={phone}
          />
        )}
      </HStack>
      <Stack
        w="100%"
        minHeight="fit-content"
        height="fit-content"
        bg="#0054CD"
        justify="start"
        py="6px"
        px="8px"
      >
        <Text
          color="white"
          textStyle="p3"
        >
          {t('header.promotionText')}
        </Text>
      </Stack>
    </CustomHeader>
  );
}

const CustomHeader = chakra('header', {
  baseStyle: {
    // height: "83px",
    // minHeight: "83px",
    // maxHeight: "104px",
    boxShadow: '0px 4px 8px 0px #AEAEAE40',
    position: 'sticky',
    top: 0,
    background: 'white',
    zIndex: 100,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
});

const DrawerMenu = ({ isLogin, name, phone }: MenuProps): React.ReactElement => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t } = useT();

  return (
    <>
      <IconButton
        id={ids.menuTrigger}
        colorScheme="teal"
        color="color-text-primary"
        onClick={onOpen}
        w="40px"
        h="40px"
        bgColor="rgba(255,255,255,.1)"
        _hover={{ bgColor: 'rgba(0,0,0,.1)' }}
        isRound
        fontSize="28px"
        icon={<MdMenu />}
        aria-label="Open menu"
      />
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={onClose}
      >
        <DrawerOverlay />
        <DrawerContent bgColor="grey.800">
          <DrawerBody color="white">
            <IconButton
              w="40px"
              h="40px"
              fontSize="24px"
              bgColor="rgba(255,255,255,.1)"
              _hover={{ bgColor: 'rgba(255,255,255,.2)' }}
              isRound
              icon={<MdClose />}
              onClick={onClose}
              aria-label="Close menu"
            />
            {isLogin && (
              <VStack
                id={ids.userInfoBlock}
                justifyContent="center"
                alignItems="flex-start"
                mt="2em"
                px="8px"
                py="12px"
                bg="grey.900"
                rounded="5px"
              >
                <Text
                  fontSize="16px"
                  fontWeight="bold"
                >
                  Hi, {name}
                </Text>
                <Text fontSize="12px">{phone}</Text>
              </VStack>
            )}
            <VStack
              alignItems="flex-start"
              py="2em"
              spacing="16px"
            >
              <Link
                href="/home"
                onClick={onClose}
                title={t('header.homePage')}
              >
                <Text
                  color="white"
                  textStyle="p2"
                >
                  {t('header.homePage')}
                </Text>
              </Link>
              <Link
                href="/blog-all"
                onClick={onClose}
                title={t('header.news')}
              >
                <Text
                  color="white"
                  textStyle="p2"
                >
                  {t('header.news')}
                </Text>
              </Link>
              {/* <Link
                href="/luggage-go"
                title={t('header.luggageGo')}
                onClick={onClose}
              >
                <Text
                  color="white"
                  textStyle="p2"
                >
                  {t('header.luggageGo')}
                </Text>
              </Link> */}

              {isLogin && (
                <>
                  <Link
                    href="/orders"
                    onClick={onClose}
                  >
                    <Text
                      color="white"
                      textStyle="p2"
                    >
                      {/* FIXME: 這個 translate key 好像有問題 */}
                      {t('orderDetails')}
                    </Text>
                  </Link>
                  <Link
                    href="/coupon"
                    onClick={onClose}
                  >
                    <Text
                      color="white"
                      textStyle="p2"
                    >
                      {t('header.coupons')}
                    </Text>
                  </Link>
                </>
              )}

              <Divider borderColor="grey.600" />
              <LinkExternal
                href="https://www.udrive.city/"
                onClick={onClose}
              >
                <Text
                  textStyle="p2"
                  color="white"
                >
                  {t('header.udrive')}
                </Text>
              </LinkExternal>
              <Flex
                direction="column"
                gap="10px"
              >
                <Text
                  textStyle="p2"
                  color="white"
                >
                  {t('header.uspaceApp')}
                </Text>
                <HStack spacing="12px">
                  <LinkExternal
                    isExternal
                    href="https://apps.apple.com/tw/app/uspace/id1454077921"
                    onClick={onClose}
                  >
                    <Image
                      width="120"
                      height="36"
                      src="/assets/download_appstore.png"
                      alt="download_appstore uspace"
                      style={{ objectFit: 'cover' }}
                    />
                  </LinkExternal>
                  <LinkExternal
                    isExternal
                    href="https://play.google.com/store/apps/details?id=com.uspace&hl=zh_TW"
                    onClick={onClose}
                  >
                    <Image
                      width="120"
                      height="36"
                      src="/assets/download_playstore.png"
                      alt="download_playstore uspace"
                      style={{ objectFit: 'cover' }}
                    />
                  </LinkExternal>
                </HStack>
              </Flex>
              {!isLogin && (
                <>
                  <Divider borderColor="grey.600" />
                  <Link
                    onClick={onClose}
                    href="/login"
                  >
                    <Text
                      color="white"
                      textStyle="p2"
                    >
                      {t('login')}
                    </Text>
                  </Link>
                </>
              )}
              {isLogin && (
                <>
                  <Divider borderColor="grey.600" />
                  <form
                    action={logout}
                    onClick={onClose}
                  >
                    <Button
                      type="submit"
                      variant="link"
                    >
                      <Text
                        color="white"
                        textStyle="p2"
                      >
                        {t('logout')}
                      </Text>
                    </Button>
                  </form>
                </>
              )}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

const LanguageMenu = (props: LanguageMenuProps): React.ReactElement => {
  const { t, i18n } = useT();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const languages = [
    { code: 'zh-TW', name: '繁體中文', flag: '🇹🇼' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
  ];

  return (
    <Menu placement="top">
      <MenuButton as={Box}>
        <Image
          width={24}
          height={24}
          src={`/assets/lang/${i18n.language}.png`}
          alt=""
        />
      </MenuButton>
      <MenuList minW="unset">
        {languages.map((lang) => (
          <MenuItem
            key={lang.code}
            onClick={() => {
              if (i18n.language !== lang.code) {
                // Redirect to the new locale while preserving the current path
                router.push(
                  `/${lang.code}${
                    pathname.startsWith(`/${i18n.language}`)
                      ? pathname.replace(`/${i18n.language}`, '')
                      : pathname
                  }${searchParams.size > 0 ? `?${searchParams.toString()}` : ''}`,
                );
              }
            }}
          >
            <Text
              color="#333333"
              fontSize="20px"
            >
              {lang.flag}
            </Text>
            <Text
              color="#333333"
              ml="8px"
            >
              {lang.name}
            </Text>
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};
